import React, { useState, useRef, useLayoutEffect } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartBar,
  faChevronDown,
  faChevronUp,
  faShoppingCart,
  faUsers,
  faCheckCircle,
  faDownload,
  faSpa,
  faBowlFood,
  faShieldAlt
} from '@fortawesome/free-solid-svg-icons';
import '../assets/styles/UXResearchProjects.scss';

const base = process.env.PUBLIC_URL;

interface ResearchProject {
  id: string;
  title: string;
  subtitle: string;
  brief: string;
  icon: any;
  pdfPath?: string;
  details: {
    aim: string;
    objectives?: string[];
    methodology?: {
      design: string;
      sampleSize: string;
      tools: string[];
      approach: string;
    };
    keyFindings: Array<{
      title: string;
      score?: string;
      insight: string;
    }>;
    recommendations: string[];
    conclusion: string;
    strengths?: string[];
    weaknesses?: string[];
  };
}

const researchProjects: ResearchProject[] = [
  {
    id: "amazon-study",
    title: "Amazon UX Research",
    subtitle: "Product Discovery to Purchase Flow Analysis",
    brief: "Quantitative study examining how product discovery, checkout experience, and re-engagement features influence purchase completion on Amazon.",
    icon: faShoppingCart,
    pdfPath: `${base}/projects/Amazon_project_UX_Research.pdf`,
    details: {
      aim: "To quantitatively assess how product discovery, checkout experience, stock availability, and re-engagement features (Wishlist / Notify Me) influence users' likelihood of completing purchases on Amazon.",
      objectives: [
        "Assess the ease of finding products on Amazon",
        "Evaluate the smoothness and clarity of the checkout process",
        "Examine user behaviour regarding purchase completion after adding items to cart",
        "Analyze how stock availability affects purchase decisions",
        "Study user engagement with Wishlist and 'Notify Me' features"
      ],
      methodology: {
        design: "Quantitative Cross-Sectional Survey",
        sampleSize: "20 Amazon users",
        tools: ["Google Forms", "Excel", "SPSS"],
        approach: "User survey + competitor feature analysis"
      },
      keyFindings: [
        { title: "Product Discovery", score: "75% satisfaction (2.75/3)", insight: "Users find products easily but expect faster filter precision" },
        { title: "Checkout Experience", score: "75% satisfaction (2.7/3)", insight: "Intuitive and trusted flow with minor usability improvements needed" },
        { title: "Purchase Completion", score: "55% completion (2.45/3)", insight: "Moderate engagement; delivery clarity influences conversion" },
        { title: "Stock Availability", score: "50% satisfaction (1.7/3)", insight: "Out-of-stock remains a pain point requiring better visibility" },
        { title: "Notify Me Feature", score: "60% engagement (2.5/3)", insight: "High user interest in re-engagement notifications" },
        { title: "Wishlist Engagement", score: "40-45% usage (2.35/3)", insight: "Moderately used; reminders could increase retention" }
      ],
      recommendations: [
        "Add 'Notify Me' restock alerts with instant email/app notifications",
        "Introduce wishlist reminders for saved items",
        "Enhance real-time stock visibility and delivery date transparency",
        "Optimize product filters and search relevance for quicker discovery",
        "Introduce AI-based re-engagement cues (e.g., 'You viewed this 3 days ago')"
      ],
      conclusion: "Amazon demonstrates high usability and trust among users, yet subtle friction points — particularly in re-engagement and stock visibility — can influence purchase completion and satisfaction. Optimizing these touchpoints can significantly boost user retention, purchase trust, and overall shopping experience."
    }
  },
  {
    id: "derma-co-study",
    title: "The Derma Co UX Evaluation",
    subtitle: "Heuristic Evaluation & Emotional Journey Analysis",
    brief: "A story-led UX heuristic evaluation examining how product discovery, ingredient complexity, and user guidance affect the skincare shopping experience.",
    icon: faSpa,
    pdfPath: `${base}/projects/Derma-co.pdf`,
    details: {
      aim: "To evaluate The Derma Co not just through heuristics, but through the emotional journey of a user seeking clarity in a cluttered skincare world.",
      keyFindings: [
        { title: "Visibility of System Status", insight: "Missing cart feedback creates user uncertainty. Users clicked 'Add to Cart' multiple times without visible confirmation." },
        { title: "Language Gap", insight: "Website speaks chemistry ('alpha arbutin', 'AHA-BHA'), but users shop for emotions ('something for acne spots', 'something gentle')." },
        { title: "Quiz Flow Issues", insight: "Skin Assessment Quiz feels restrictive with no back button, skip option, or progress indicators." },
        { title: "Cognitive Overload", insight: "Long ingredient lists, percentages, icons, and disclaimers create heavy mental work for users." },
        { title: "Missing Safety Alerts", insight: "No warnings for mixing harsh actives (Retinol + Vitamin C, AHA + BHA) could cause real harm and distrust." }
      ],
      recommendations: [
        "Add ingredient conflict warnings to protect users and build trust",
        "Simplify language with plain-English toggles: 'Explain Like I'm New to Skincare'",
        "Improve micro-interactions: add-to-cart confirmation, loading indicators, quiz progress",
        "Rework quiz flow with back button, skip option, progress bar, and softer language",
        "Reduce homepage clutter focusing on Problem → Solution → Product",
        "Offer routine bundles for 3-click purchase to lower cognitive load"
      ],
      conclusion: "Users shop for hope and confidence. The Derma Co can transform friction-heavy UX into an emotionally supportive, trust-building journey through clearer language, feedback, and safety guidance.",
      strengths: [
        "Strong branding",
        "Trustworthy visuals",
        "Good product credibility",
        "Smooth checkout once items are chosen"
      ],
      weaknesses: [
        "Ingredient overload",
        "No useful feedback (cart, loading)",
        "Confusing, rigid quiz flow",
        "Homepage clutter",
        "No ingredient conflict alerts"
      ]
    }
  },
  {
    id: "maggi-masala-study",
    title: "Maggi Masala UX Research",
    subtitle: "Masala Preference & Customization Behavior Study",
    brief: "Quantitative research understanding user preferences between one vs two masala packets and exploring customization behaviors around Maggi consumption.",
    icon: faBowlFood,
    pdfPath: `${base}/projects/Maggi_Masala_Full_Study.pdf`,
    details: {
      aim: "To understand preference between one vs two masala packets and user behaviors around Maggi consumption.",
      objectives: [
        "Identify masala preference patterns",
        "Understand taste satisfaction levels",
        "Explore customization behaviors",
        "Capture emotional connections with the brand",
        "Spot opportunities for new variants"
      ],
      methodology: {
        design: "Quantitative Survey",
        sampleSize: "30 users",
        tools: ["Google Forms"],
        approach: "Percentages + behavioral insights analysis"
      },
      keyFindings: [
        { title: "Two Masala Packets Preferred", score: "58% preference", insight: "Majority prefer double masala for more satisfying flavor intensity" },
        { title: "One Masala Classic", score: "32% preference", insight: "Balanced tasters prefer classic taste with milder flavors" },
        { title: "Creative Customizers", score: "10% preference", insight: "Some mix half masala with homemade seasoning for personalization" },
        { title: "Flavor Intensity", score: "4.6/5 rating", insight: "Strong demand for bold flavors drives satisfaction" },
        { title: "Nostalgia Factor", insight: "Emotional connections to 'double masala' moments (hostel days, late nights)" }
      ],
      recommendations: [
        "Launch Lite / Classic / Double Masala variants",
        "Introduce 'Build Your Masala' customizable packs",
        "Add pairing suggestion icons (egg, cheese, veggies)",
        "Release flavour shot sachets (spicy, tangy, smoky, cheesy)",
        "Use nostalgia-driven campaign themes"
      ],
      conclusion: "Flavor intensity and emotional resonance drive engagement. Customizable masala formats can amplify delight, loyalty, and repeat usage."
    }
  },
  {
    id: "mygate-study",
    title: "Anacity Gatekeeper Study",
    subtitle: "Micro UX Study on Task Completion",
    brief: "Task-based usability test evaluating how easily residents and guards perform key security tasks in the gatekeeper app.",
    icon: faShieldAlt,
    pdfPath: `${base}/projects/Anacity-gatekeeper.pdf`,
    details: {
      aim: "Evaluate how easily residents and guards perform key tasks in the gatekeeper security application.",
      methodology: {
        design: "Task-based Usability Test",
        sampleSize: "7 participants (5 residents, 2 guards)",
        tools: ["In-app testing", "User observation"],
        approach: "Task completion analysis with qualitative feedback"
      },
      keyFindings: [
        { title: "Icon Confusion", insight: "Icon-only actions lead to mis-taps & slower decisions for guards" },
        { title: "Hidden Entry Logs", insight: "Logs buried in navigation slow urgent lookup" },
        { title: "SOS Feedback Missing", insight: "No confirmation reduces confidence in emergency actions" }
      ],
      recommendations: [
        "Replace ambiguous icons with labeled Allow / Deny buttons",
        "Surface Entry Logs on home screen",
        "Add full-screen SOS confirmation with vibration/haptic feedback",
        "Increase tap target sizes for guard UI",
        "Use labeled navigation over icon-only"
      ],
      conclusion: "Clear labels, surfaced logs, and feedback-heavy SOS flows improve trust, speed, and security confidence."
    }
  }
];

function UXResearchProjects() {
  const [expandedProject, setExpandedProject] = useState<string | null>(null);
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const lastExpanded = useRef<string | null>(null);

  const toggleProject = (projectId: string) => {
    setExpandedProject(prev => (prev === projectId ? null : projectId));
  };

  // Scroll to top of newly expanded card after layout paints
  useLayoutEffect(() => {
    if (expandedProject && expandedProject !== lastExpanded.current) {
      const el = cardRefs.current[expandedProject];
      if (el) {
        const TOP_OFFSET = 80; // adjust if you have a fixed nav
        const y = el.getBoundingClientRect().top + window.pageYOffset - TOP_OFFSET;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }
    lastExpanded.current = expandedProject;
  }, [expandedProject]);

  return (
    <div className="container" id="ux-research-projects">
      <div className="ux-research-container">
        <h1>
          <FontAwesomeIcon icon={faChartBar} className="title-icon" />
          UX Research Projects
        </h1>

        <div className="research-projects-grid">
          {researchProjects.map(project => {
            const isExpanded = expandedProject === project.id;
            return (
              <div
                key={project.id}
                ref={el => { cardRefs.current[project.id] = el; }}
                className={`research-project-card ${isExpanded ? 'expanded' : ''}`}
              >
                <div
                  className="project-header"
                  onClick={() => toggleProject(project.id)}
                  role="button"
                  aria-expanded={isExpanded}
                  aria-controls={`details-${project.id}`}
                >
                  <div className="header-left">
                    <FontAwesomeIcon icon={project.icon} size="2x" className="project-icon" />
                    <div className="header-text">
                      <h2>{project.title}</h2>
                      <h3>{project.subtitle}</h3>
                      <p className="brief">{project.brief}</p>
                      {project.pdfPath && (
                        <a
                          href={project.pdfPath}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="download-link"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <FontAwesomeIcon icon={faDownload} />
                          View Full Research Report (PDF)
                        </a>
                      )}
                    </div>
                  </div>
                  <FontAwesomeIcon
                    icon={isExpanded ? faChevronUp : faChevronDown}
                    className="toggle-icon"
                  />
                </div>

                {isExpanded && (
                  <div
                    id={`details-${project.id}`}
                    className="project-details"
                  >
                    <div className="detail-section">
                      <h4>Research Aim</h4>
                      <p>{project.details.aim}</p>
                    </div>

                    {project.details.objectives && (
                      <div className="detail-section">
                        <h4>Objectives</h4>
                        <ul>
                          {project.details.objectives.map((obj, idx) => (
                            <li key={idx}>
                              <FontAwesomeIcon icon={faCheckCircle} className="bullet-icon" />
                              {obj}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {project.details.methodology && (
                      <div className="detail-section methodology">
                        <h4>Methodology</h4>
                        <div className="methodology-grid">
                          <div className="method-item">
                            <strong>Research Design:</strong> {project.details.methodology.design}
                          </div>
                          <div className="method-item">
                            <strong>Sample Size:</strong> {project.details.methodology.sampleSize}
                          </div>
                          <div className="method-item">
                            <strong>Tools:</strong> {project.details.methodology.tools.join(", ")}
                          </div>
                          <div className="method-item">
                            <strong>Approach:</strong> {project.details.methodology.approach}
                          </div>
                        </div>
                      </div>
                    )}

                    {project.details.strengths && project.details.weaknesses && (
                      <div className="detail-section">
                        <h4>Strengths & Weaknesses</h4>
                        <div className="findings-grid">
                          <div className="finding-card">
                            <h5>✓ Strengths</h5>
                            <ul className="compact-list">
                              {project.details.strengths.map((strength, idx) => (
                                <li key={idx}>{strength}</li>
                              ))}
                            </ul>
                          </div>
                          <div className="finding-card">
                            <h5>✗ Weaknesses</h5>
                            <ul className="compact-list">
                              {project.details.weaknesses.map((weakness, idx) => (
                                <li key={idx}>{weakness}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="detail-section">
                      <h4>Key Findings</h4>
                      <div className="findings-grid">
                        {project.details.keyFindings.map((finding, idx) => (
                          <div key={idx} className="finding-card">
                            <h5>{finding.title}</h5>
                            {finding.score && <div className="score">{finding.score}</div>}
                            <p>{finding.insight}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="detail-section">
                      <h4>Recommendations</h4>
                      <ul className="recommendations">
                        {project.details.recommendations.map((rec, idx) => (
                          <li key={idx}>
                            <FontAwesomeIcon icon={faUsers} className="bullet-icon" />
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="detail-section conclusion">
                      <h4>Conclusion</h4>
                      <p>{project.details.conclusion}</p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default UXResearchProjects;